/**
 * What the artist's estate can see — OND-58, and the owner's two switches.
 *
 * TWO CONTROLS AND NOT ONE, which is the ticket's last rule drawn: "naming the
 * owner by default does not itself constitute a grant of full record access."
 * Each switch has its own label and its own sentence, and no sentence is shared
 * between them — a reader must never be able to take one line as describing
 * both.
 *
 * It renders nothing at all for anybody but the owner, because the controller
 * hands it null: `ondo.work_estate_controls()` returns no row for a non-owner,
 * so there is no panel to draw rather than a panel drawn disabled.
 */
import { ESTATE_DISCLOSURE_COPY } from "./work.constants.ts";
import type { EstateDisclosurePanel } from "./work.model.ts";

import "./work.css";

type Props = {
  panel: EstateDisclosurePanel | null;
  busy: boolean;
  failed: boolean;
  onSetHidden: (hidden: boolean) => void;
  onSetRecordOpen: (open: boolean) => void;
};

export function WorkEstatePanel({
  panel,
  busy,
  failed,
  onSetHidden,
  onSetRecordOpen,
}: Props) {
  if (panel === null) return null;

  return (
    <section className="work__panel work-estate" aria-labelledby="work-estate-heading">
      <h3 className="work__panel-head" id="work-estate-heading">
        {ESTATE_DISCLOSURE_COPY.heading}
      </h3>
      <p className="work-estate__lead">{ESTATE_DISCLOSURE_COPY.lead}</p>

      {/* THE IDENTITY SWITCH IS WRITTEN POSITIVELY — 'Name me as the owner',
          checked by default — rather than as 'Hide my name'. The product
          default is named, and a switch whose off position is the default
          reads correctly at a glance; an inverted one makes the common state
          look like something the owner turned on. The model holds `hidden`
          because that is what the server stores, and the inversion happens
          here, once. */}
      <label className="work-estate__row">
        <input
          type="checkbox"
          checked={!panel.hidden}
          disabled={busy || !panel.canGrant}
          onChange={(event) => onSetHidden(!event.target.checked)}
        />
        <span className="work-estate__row-body">
          <span className="work-estate__row-title">
            {ESTATE_DISCLOSURE_COPY.identityTitle}
          </span>
          <span className="work-estate__row-note">{panel.identityLine}</span>
        </span>
      </label>

      <label className="work-estate__row">
        <input
          type="checkbox"
          checked={panel.recordOpen}
          disabled={busy || !panel.canGrant}
          onChange={(event) => onSetRecordOpen(event.target.checked)}
        />
        <span className="work-estate__row-body">
          <span className="work-estate__row-title">
            {ESTATE_DISCLOSURE_COPY.accessTitle}
          </span>
          <span className="work-estate__row-note">{panel.accessLine}</span>
        </span>
      </label>

      {/* `role="status"` on the saving line and `role="alert"` on the failure,
          because they are different urgencies: one is progress and one is that
          nothing happened. The failure says the settings are UNCHANGED, which
          is true — the controller only adopts what the server sent back, so a
          refused write leaves both switches where they were. */}
      {busy && (
        <p className="work-estate__state" role="status">
          {ESTATE_DISCLOSURE_COPY.saving}
        </p>
      )}
      {failed && !busy && (
        <p className="work-estate__state work-estate__state--failed" role="alert">
          {ESTATE_DISCLOSURE_COPY.failed}
        </p>
      )}
    </section>
  );
}
